// @flow

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { actions as toastrActions, toastr } from 'react-redux-toastr';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import {
  formatDateDB,
  toMoment,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import FloorPlanService from '../../../services/floorPlanService';
import FloorPlanPricingService from '../../../services/floorPlanPricingService';
import GrossRentsService from '../../../services/grossRentsService';
import useIntl from '../../../hooks/useIntl';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import { extractErrorMessage } from '../../../utils/errors';
import { processFloorPlanWithPricingResult } from '../../ManageFloorPlans/HudLimitsModal/utils';
import type {
  AddGrossRentLimitsFormValues,
  AddGrossRentLimitsWizardProps,
} from './types';
import type { GrossRentChangeGroup } from '../GrossRentChangesTab/types';
import messages from './messages';
import {
  ACTIVE_STEP_INDEX_FIRST,
  ACTIVE_STEP_INDEX_LAST,
  DEFAULT_FORM_VALUES,
  WIZARD_STEP,
} from './constants';
import {
  buildBatchPayload,
  buildEditChangesPayload,
  buildEditDatesPayload,
  buildEditDefaultFormValues,
  computeAnticipatedVoucherMonthForGrossRent,
  deriveGrcExistsForYearFromCertifications,
  isStepOneValid,
  mapHudFloorPlanRows,
  parseDbDateLocal,
} from './utils';

const NO_CHANGES_ERROR = 'NO_CHANGES';

const showToast = ({
  type,
  title,
  message,
}: {
  type?: string,
  title: string,
  message: string,
}) => {
  if (type === 'error') {
    toastr.error(title, message);
    return;
  }

  toastr.success(title, message);
};

const GR_LIMITS_IN_PROGRESS_TOAST_ID = 'add-gross-rent-limits-in-progress';

const floorPlanService = new FloorPlanService();
const grossRentsService = new GrossRentsService();
const floorPlanPricingService = new FloorPlanPricingService();
const STALE_TIME_MS = 60_000;

export const useHudFloorPlansWithPricing = (
  open: boolean,
  priorValuesEffectiveDate: ?Date = null,
) => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const queryKey = ['hudFloorPlansWithPricing', organizationId, propertyId];

  const queryOptions = useMemo(
    () => ({
      enabled: open && Boolean(organizationId && propertyId),
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME_MS,
      onError: (error) => {
        showToast({
          type: 'error',
          title: intl.formatMessage(messages.errorLoadingFloorPlans),
          message: extractErrorMessage(error),
        });
      },
    }),
    [open, organizationId, propertyId, intl],
  );

  const { data, isLoading, isError } = useQuery(
    queryKey,
    ({ signal }) =>
      floorPlanService.getAllWithPricing(organizationId, propertyId, {
        signal,
      }),
    queryOptions,
  );

  const rawFloorPlans = useMemo(
    () => processFloorPlanWithPricingResult(data ?? []),
    [data],
  );

  const floorPlans = useMemo(
    () => mapHudFloorPlanRows(data ?? [], priorValuesEffectiveDate),
    [data, priorValuesEffectiveDate],
  );

  return {
    floorPlans,
    rawFloorPlans,
    isLoading,
    isError,
  };
};

export const useAnticipatedVoucherMonth = (
  effectiveDate: ?Date,
  enabled: boolean,
) => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const effectiveDateParam = effectiveDate ? formatDateDB(effectiveDate) : null;

  const queryOptions = useMemo(
    () => ({
      enabled:
        enabled && Boolean(organizationId && propertyId && effectiveDateParam),
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME_MS,
      retry: false,
      onError: (error) => {
        showToast({
          type: 'error',
          title: intl.formatMessage(
            messages.errorLoadingAnticipatedVoucherMonth,
          ),
          message: extractErrorMessage(error),
        });
      },
    }),
    [enabled, organizationId, propertyId, effectiveDateParam, intl],
  );

  const { data, isLoading, isSuccess } = useQuery(
    ['anticipatedVoucherMonth', organizationId, propertyId, effectiveDateParam],
    ({ signal }) =>
      grossRentsService.getAnticipatedVoucherMonth(
        organizationId,
        propertyId,
        effectiveDateParam ?? '',
        { signal },
      ),
    queryOptions,
  );

  const anticipatedVoucherMonth = useMemo(() => {
    if (!effectiveDate) {
      return null;
    }

    if (isSuccess && data?.anticipatedVoucherMonth) {
      return parseDbDateLocal(data.anticipatedVoucherMonth);
    }

    return computeAnticipatedVoucherMonthForGrossRent(effectiveDate);
  }, [data, effectiveDate, isSuccess]);

  return {
    anticipatedVoucherMonth,
    isLoading,
  };
};

export const useGrossRentCertExistsForYear = (
  effectiveDate: ?Date,
  enabled: boolean,
) => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const year = effectiveDate ? toMoment(effectiveDate).year() : null;

  const queryOptions = useMemo(
    () => ({
      enabled: enabled && Boolean(organizationId && propertyId && year),
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME_MS,
      retry: false,
      onError: (error) => {
        showToast({
          type: 'error',
          title: intl.formatMessage(messages.errorCheckingGrossRentCertExists),
          message: extractErrorMessage(error),
        });
      },
    }),
    [enabled, organizationId, propertyId, year, intl],
  );

  const { data, isLoading } = useQuery(
    ['grossRentCertExistsForYear', organizationId, propertyId, year],
    async ({ signal }) => {
      try {
        const result = await grossRentsService.getGrossRentCertExistsForYear(
          organizationId,
          propertyId,
          year ?? 0,
          { signal },
        );

        return Boolean(result?.exists);
      } catch (_error) {
        // Fallback to the list endpoint until a dedicated "exists for year" read endpoint is available.
        const certifications =
          await grossRentsService.getGrossRentCertifications(
            organizationId,
            propertyId,
            { signal },
          );

        return deriveGrcExistsForYearFromCertifications(
          certifications ?? [],
          year ?? 0,
        );
      }
    },
    queryOptions,
  );

  return {
    exists: Boolean(data),
    isLoading,
  };
};

export const useSubmitGrossRentLimits = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';

  const { mutateAsync, isLoading } = useMutation(
    ['submitGrossRentLimits', organizationId, propertyId],
    async ({
      values,
      rawFloorPlans,
    }: {
      values: AddGrossRentLimitsFormValues,
      rawFloorPlans: Array<Object>,
    }) => {
      const payload = buildBatchPayload(values, rawFloorPlans);

      if (payload.length === 0) {
        throw new Error(NO_CHANGES_ERROR);
      }

      // Returned so onSuccess can read certificationsCreated.
      return floorPlanPricingService.batchCreate(
        organizationId,
        propertyId,
        payload,
      );
    },
    {
      onMutate: () => {
        dispatch(
          toastrActions.add({
            id: GR_LIMITS_IN_PROGRESS_TOAST_ID,
            type: 'info',
            title: intl.formatMessage(messages.processingToastTitle),
            message: intl.formatMessage(messages.processingToastMessage),
            options: {
              timeOut: 0,
              className: 'rrt-purple',
              removeOnHover: false,
              showCloseButton: true,
            },
          }),
        );
      },
      onSuccess: (response) => {
        dispatch(toastrActions.remove(GR_LIMITS_IN_PROGRESS_TOAST_ID));
        queryClient.invalidateQueries([
          'grossRentChanges',
          organizationId,
          propertyId,
        ]);
        queryClient.invalidateQueries([
          'grossRentCertifications',
          organizationId,
          propertyId,
        ]);
        queryClient.invalidateQueries([
          'hudFloorPlansWithPricing',
          organizationId,
          propertyId,
        ]);
        // The limit saved either way, so only the message changes. An API that does not report the
        // count keeps the original message rather than claiming none were created.
        const certificationsCreated = response?.certificationsCreated;
        showToast({
          type: 'success',
          title: intl.formatMessage(messages.successTitle),
          message: intl.formatMessage(
            certificationsCreated === 0
              ? messages.noCertificationsMessage
              : messages.successMessage,
          ),
        });
      },
      onError: (error) => {
        dispatch(toastrActions.remove(GR_LIMITS_IN_PROGRESS_TOAST_ID));
        if (error.message === NO_CHANGES_ERROR) {
          showToast({
            type: 'error',
            title: intl.formatMessage(messages.errorSaving),
            message: intl.formatMessage(messages.errorNoChangesToSave),
          });
          return;
        }

        // A row-level failure can leave part of the batch saved;
        // refresh both tabs and the wizard's floor plan pricing either way.
        queryClient.invalidateQueries([
          'grossRentChanges',
          organizationId,
          propertyId,
        ]);
        queryClient.invalidateQueries([
          'grossRentCertifications',
          organizationId,
          propertyId,
        ]);
        queryClient.invalidateQueries([
          'hudFloorPlansWithPricing',
          organizationId,
          propertyId,
        ]);
        showToast({
          type: 'error',
          title: intl.formatMessage(messages.errorSaving),
          message: extractErrorMessage(error),
        });
      },
    },
  );

  return {
    submitGrossRentLimits: mutateAsync,
    isSubmitting: isLoading,
  };
};

export const useSubmitEditGrossRentChange = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';

  const { mutateAsync, isLoading } = useMutation(
    ['submitEditGrossRentChange', organizationId, propertyId],
    async ({
      values,
      editingChange,
      rawFloorPlans,
    }: {
      values: AddGrossRentLimitsFormValues,
      editingChange: GrossRentChangeGroup,
      rawFloorPlans: Array<Object>,
    }) => {
      const changes = buildEditChangesPayload(
        values,
        editingChange,
        rawFloorPlans,
      );
      const datesPayload = buildEditDatesPayload(values, editingChange);

      if (changes.length === 0 && Object.keys(datesPayload).length === 0) {
        throw new Error(NO_CHANGES_ERROR);
      }

      await grossRentsService.updateGrossRentChange(
        organizationId,
        propertyId,
        {
          ...datesPayload,
          effectiveDate: editingChange.effectiveDate,
          changes,
        },
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'grossRentChanges',
          organizationId,
          propertyId,
        ]);
        queryClient.invalidateQueries([
          'grossRentCertifications',
          organizationId,
          propertyId,
        ]);
        queryClient.invalidateQueries([
          'hudFloorPlansWithPricing',
          organizationId,
          propertyId,
        ]);
        showToast({
          type: 'success',
          title: intl.formatMessage(messages.successTitle),
          message: intl.formatMessage(messages.editSuccessMessage),
        });
      },
      onError: (error) => {
        if (error.message === NO_CHANGES_ERROR) {
          showToast({
            type: 'error',
            title: intl.formatMessage(messages.errorSaving),
            message: intl.formatMessage(messages.errorNoChangesToSave),
          });
          return;
        }

        showToast({
          type: 'error',
          title: intl.formatMessage(messages.errorSaving),
          message: extractErrorMessage(error),
        });
      },
    },
  );

  return {
    submitEditGrossRentChange: mutateAsync,
    isSubmitting: isLoading,
  };
};

export const useAddGrossRentLimitsWizard = ({
  open,
  closeModal,
  onSuccess,
  editingChange,
}: AddGrossRentLimitsWizardProps) => {
  const isEditMode = Boolean(editingChange);
  const [activeStep, setActiveStep] = useState(ACTIVE_STEP_INDEX_FIRST);
  const reactHookFormProps = useReactHookFormProps({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onBlur',
  });
  const { reset, watch, getValues, errors } = reactHookFormProps;
  const { rawFloorPlans } = useHudFloorPlansWithPricing(open);
  const { submitGrossRentLimits, isSubmitting: isSubmittingAdd } =
    useSubmitGrossRentLimits();
  const { submitEditGrossRentChange, isSubmitting: isSubmittingEdit } =
    useSubmitEditGrossRentChange();
  const isSubmitting = isSubmittingAdd || isSubmittingEdit;

  // Prefill from the saved change when opening in edit mode (same reset-on-open
  // pattern as EditGrossRentCertificationModal).
  useEffect(() => {
    if (!open || !isEditMode) {
      return;
    }

    reset(buildEditDefaultFormValues(editingChange));
    setActiveStep(ACTIVE_STEP_INDEX_FIRST);
  }, [open, isEditMode, editingChange, reset]);

  const [effectiveDate, anticipatedVoucherMonth, floorPlanChanges] = watch([
    'effectiveDate',
    'anticipatedVoucherMonth',
    'floorPlanChanges',
  ]);
  // Gate Next on the same diffs used at submit time (buildEditChangesPayload +
  // buildEditDatesPayload) rather than react-hook-form's whole-form isDirty.
  // isDirty also trips on the read-only anticipatedVoucherMonth auto-fill
  // (Step1.hooks.js sets it via setValue once effectiveDate resolves), which
  // would surface as a false-positive "dirty" form on reopen, with no real
  // edit made, if we compared against isDirty instead of these diffs.
  const hasEditChanges = isEditMode
    ? buildEditChangesPayload(
        { effectiveDate, anticipatedVoucherMonth, floorPlanChanges },
        editingChange,
        rawFloorPlans,
      ).length > 0 ||
      Object.keys(
        buildEditDatesPayload(
          { effectiveDate, anticipatedVoucherMonth },
          editingChange,
        ),
      ).length > 0
    : false;
  // isStepOneValid doesn't see the AVD field's own react-hook-form errors.
  const isAnticipatedVoucherMonthValid = !errors?.anticipatedVoucherMonth;
  const isStepOneComplete = isEditMode
    ? isStepOneValid({
        effectiveDate,
        anticipatedVoucherMonth,
        floorPlanChanges,
      }) &&
      hasEditChanges &&
      isAnticipatedVoucherMonthValid
    : isStepOneValid({
        effectiveDate,
        anticipatedVoucherMonth,
        floorPlanChanges,
      }) && isAnticipatedVoucherMonthValid;

  const resetWizard = () => {
    reset(DEFAULT_FORM_VALUES);
    setActiveStep(ACTIVE_STEP_INDEX_FIRST);
  };

  const onClose = (ignoreSubmitting: boolean = false) => {
    if (!ignoreSubmitting && isSubmitting) {
      return;
    }

    closeModal();
    resetWizard();
  };

  const onBack = () => {
    if (activeStep === ACTIVE_STEP_INDEX_FIRST) {
      onClose();
      return;
    }

    setActiveStep(activeStep - 1);
  };

  const onContinue = async () => {
    if (activeStep === ACTIVE_STEP_INDEX_LAST) {
      try {
        if (isEditMode) {
          await submitEditGrossRentChange({
            values: getValues(),
            editingChange,
            rawFloorPlans,
          });
        } else {
          await submitGrossRentLimits({
            values: getValues(),
            rawFloorPlans,
          });
        }
        onClose(true);
        if (onSuccess) {
          onSuccess();
        }
      } catch (_error) {
        // ponytail: errors toasted in the submit hooks' onError
      }
      return;
    }

    setActiveStep(activeStep + 1);
  };

  const isFirstStep = activeStep === WIZARD_STEP.NEW_GROSS_RENT_LIMITS;
  const isLastStep = activeStep === WIZARD_STEP.CONFIRMATION;

  return {
    activeStep,
    disabled: isSubmitting,
    isEditMode,
    isFirstStep,
    isLastStep,
    isStepOneComplete,
    isSubmitting,
    onBack,
    onClose,
    onContinue,
    reactHookFormProps,
  };
};
